import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import PaperContainer from "../../../../components/Containers/PaperContainer";
import DynamicPaginatedTable from "../../../../components/Tables/DynamicPaginatedTable";
import {
    setBlogSubscriberCurrentPage,
    setBlogSubscriberPageLimit,
    setBlogSubscriberTotalCount,
    setBlogSubscriberTotalPages
} from "../../../../lib/slices/pagination/blogSubscriberPageSlice";
import { getAllBlogSubscribers } from "../../../../services/blogsApiFn";
import { handleOpenSnackBar } from "../../../../utils";
import BlogSubscribersFilters from "./Filters";
import useDebounce from "../../../../hooks/useDebounce";

const headerData = [
    "subscribedAt",
    "email",
    // "actions",
];

const tableRows = [
    "created_date",
    "email",
    // "actions",
];

function BlogSubscribersList() {
    // Reference to store the fetch ID
    const fetchIdRef = useRef(0);
    // Dispatch Hook
    const dispatch = useDispatch();
    // Selector Hook
    const { t } = useTranslation(["blog", "common"]);
    // State to store the blogs
    const [allSubscribers, setAllSubscribers] = useState([]);
    // State to store the loading state
    const [isLoading, setIsLoading] = useState(false);
    // State to store the pagination
    const pagination = useSelector((state) => state.blogSubscriberPaginationReducer);
    // Search Keys
    const searchKeys = useSelector((state) => state.searchKeysReducer.searchKeys);
    // Use Debounce Hook
    const debouncedSearchTerm = useDebounce(searchKeys.blogSubscriberEmail, 1000, true, () => {
        dispatch(setBlogSubscriberCurrentPage(1));
    });

    // Function to fetch the blogs
    const fetchAllSubscribers = async (page, limit, search) => {
        const fetchId = ++fetchIdRef.current;
        try {

            setIsLoading(true);
            const response = await getAllBlogSubscribers({ page, limit, search });
            if (fetchId !== fetchIdRef.current) return;
            if (response?.data?.status === "success") {
                const subscribers = response?.data?.data?.data || [];
                const paginationData = response?.data?.data?.pagination || {};
                setAllSubscribers(subscribers);
                dispatch(setBlogSubscriberPageLimit(paginationData.limit || limit));
                dispatch(setBlogSubscriberCurrentPage(paginationData.page || page));
                dispatch(
                    setBlogSubscriberTotalCount(
                        paginationData.total ?? paginationData.totalSubscribers ?? subscribers.length
                    )
                );
                dispatch(setBlogSubscriberTotalPages(paginationData.totalPages ?? 1));
            } else {
                handleOpenSnackBar("error", response?.data?.message);
            }
        } catch (error) {
            if (fetchId !== fetchIdRef.current) return;
            handleOpenSnackBar("error", error?.message);
        } finally {
            if (fetchId === fetchIdRef.current) {
                setIsLoading(false);
            }
        }
    };

    // Effect to fetch the blogs
    useEffect(() => {
        fetchAllSubscribers(pagination.page, pagination.limit, debouncedSearchTerm);
    }, [pagination.page, pagination.limit, debouncedSearchTerm]);

    // Function to handle the page change
    const handlePageChange = (event, newPage) => {
        dispatch(setBlogSubscriberCurrentPage(newPage + 1));
    };

    // Function to handle the rows per page change
    const handleRowsPerPageChange = (event) => {
        const limit = parseInt(event.target.value, 10);
        dispatch(setBlogSubscriberPageLimit(limit));
        dispatch(setBlogSubscriberCurrentPage(1));
    };


    const renderTable = useMemo(() => {
        return (
            <DynamicPaginatedTable
                tableHeader={headerData}
                displayRows={tableRows}
                tableData={allSubscribers}
                paginatedData={pagination}
                showDynamicPagination={true}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
                rowsPerPageOptions={[10, 20, 30, 50, 100]}
                isLoading={isLoading}
            />
        );
    }, [allSubscribers, isLoading]);

    return (
        <PaperContainer
            title={t("blogSubscribers")}
            subtitle={t("blogSubscribersDescription")}
            searchActions={<BlogSubscribersFilters />}
        >
            {renderTable}
        </PaperContainer>
    );
}

export default BlogSubscribersList;

